import { AddonServiceProduct } from 'models/AddonServiceProduct';
import { Action } from './reducer';
import { postCheckInstallationZipCode } from 'services/ApiProductPageService';
import { getFormattedZipCode } from 'components/Shared/ZipCodeInput/themes/BGH/validation';
import { hasElements } from 'utils';
import { AddonProduct } from 'models/AddonProduct';
import { Dispatch } from 'redux';
import { GTMInstallationZipCode } from 'store/googleTagManager/actions';

export const isValidInstallation = (
  addonServiceProduct: AddonServiceProduct,
  validInstallations: string[]
) => {
  if (addonServiceProduct.installationId === '') {
    // No installation ID means it is avalible for all zip codes.
    return true;
  }

  return (
    addonServiceProduct.installationId === undefined ||
    validInstallations.some(installation => installation === addonServiceProduct.installationId)
  );
};

const getAddonIds = (addonService: AddonServiceProduct): string[] => {
  const res: string[] = addonService.installationId ? [addonService.installationId] : [];

  if (hasElements(addonService.addonServices)) {
    addonService.addonServices.forEach(addon => {
      res.push(...getAddonIds(addon));
    });
  }

  return res;
};

export const getAllUniqueIds = (addonServices: AddonServiceProduct[] | undefined): string[] => {
  const allIds: string[] = [];

  if (addonServices === undefined) {
    return allIds;
  }

  addonServices.forEach(service => {
    allIds.push(...getAddonIds(service));
  });

  const uniqueIds = Array.from(new Set(allIds));
  return uniqueIds;
};

export const postZipCode = (
  zipCode: string,
  installationIds: string[],
  dispatch: React.Dispatch<Action>,
  reduxDispatch: Dispatch
) => {
  dispatch({ type: 'fetchValidInstallationIds' });
  const formattedZipCode = getFormattedZipCode(zipCode);

  postCheckInstallationZipCode({
    zipCode: formattedZipCode,
    installationIds
  })
    .then(res => {
      //If the result contains any installation Ids the zip code is valid for installations
      reduxDispatch(GTMInstallationZipCode(res.installationIds.length > 0));

      dispatch({
        type: 'fetchValidInstallationIdsSuccess',
        installationIds: res.installationIds
      });
    })
    .catch(() => {
      dispatch({
        type: 'fetchValidInstallationIdsError'
      });
    });
};

const hasChildToBeSelected = (
  productArray: AddonServiceProduct[] | undefined,
  idToBeSelected: string
): boolean => {
  if (!hasElements(productArray)) {
    return false;
  }

  return productArray.some(product => {
    if (product.productId === idToBeSelected) {
      return true;
    }

    return hasChildToBeSelected(product.addonServices, idToBeSelected);
  });
};

const deselectAllChildren = (children: AddonServiceProduct[]): AddonServiceProduct[] => {
  return children.map(child => {
    return {
      ...child,
      selected: false,
      addonServices: hasElements(child.addonServices)
        ? deselectAllChildren(child.addonServices)
        : undefined
    };
  });
};

const selectProductByIdRecursive = (
  productArray: AddonServiceProduct[],
  id: string
): AddonServiceProduct[] => {
  return productArray.map(product => {
    if (hasChildToBeSelected(product.addonServices, id) && product.selected) {
      return {
        ...product,
        addonServices: hasElements(product.addonServices)
          ? selectProductByIdRecursive(product.addonServices, id)
          : undefined
      };
    }

    return {
      ...product,
      selected: !product.selected && product.productId === id ? true : product.selected,
      addonServices:
        hasElements(product.addonServices) && !product.selected
          ? deselectAllChildren(product.addonServices)
          : product.addonServices
    };
  });
};

export const selectProductById = (
  productArray: AddonServiceProduct[],
  id: string
): AddonServiceProduct[] => {
  return productArray.map(product => {
    if (hasChildToBeSelected(product.addonServices, id) && product.selected) {
      return {
        ...product,
        addonServices: hasElements(product.addonServices)
          ? selectProductByIdRecursive(product.addonServices, id)
          : undefined
      };
    }

    return {
      ...product,
      selected: product.productId === id,
      addonServices: hasElements(product.addonServices)
        ? deselectAllChildren(product.addonServices)
        : undefined
    };
  });
};

export const deselectProductById = (
  productArray: AddonServiceProduct[],
  id: string
): AddonServiceProduct[] => {
  return productArray.map(product => {
    if (product.productId === id) {
      return {
        ...product,
        selected: false,
        addonServices: hasElements(product.addonServices)
          ? deselectAllChildren(product.addonServices)
          : undefined
      };
    }

    return {
      ...product,
      addonServices: hasElements(product.addonServices)
        ? deselectProductById(product.addonServices, id)
        : undefined
    };
  });
};

export const deselectAllProducts = (productArray: AddonServiceProduct[]): AddonServiceProduct[] => {
  return productArray.map(product => {
    return {
      ...product,
      selected: false,
      addonServices: hasElements(product.addonServices)
        ? deselectAllProducts(product.addonServices)
        : undefined
    };
  });
};

export const getAllSelectedProducts = (productArray: AddonServiceProduct[]): AddonProduct[] => {
  const res: AddonProduct[] = [];

  productArray.forEach(product => {
    if (product.selected) {
      res.push({ id: product.productId, quantity: product.quantity.amount });
    }
    if (hasElements(product.addonServices)) {
      res.push(...getAllSelectedProducts(product.addonServices));
    }
  });

  return res;
};

export const getPPIFileName = (productArray: AddonServiceProduct[]): string => {
  const prePurchaseFileName = productArray.find(
    product => product.prePurchaseFileName !== undefined
  )?.prePurchaseFileName;

  return prePurchaseFileName !== undefined ? prePurchaseFileName : '';
};

export const getPPIFilePath = (productArray: AddonServiceProduct[]): string => {
  const prePurchaseFilePath = productArray.find(
    product => product.prePurchaseFilePath !== undefined
  )?.prePurchaseFilePath;

  return prePurchaseFilePath !== undefined ? prePurchaseFilePath : '';
};
